// 3D emoji (Microsoft Fluent Emoji 3D, MIT licence) served from our own /emoji3d folder - no third-party CDN, so it is fast
// (same Cloudflare edge as the app, cached for a year) and keeps working if an outside service goes down.
// Anything we have no picture for simply stays as the normal phone emoji.
import { EMOJI3D_FILES } from '@/lib/emoji3d-manifest'

const hex = (cp: number) => cp.toString(16)
const FE0F = 0xfe0f

/** File name (no extension) for ONE emoji character / sequence, or '' if there is no 3D picture for it. */
export function emoji3dName(g: string): string {
  const cps = Array.from(g).map((c) => c.codePointAt(0)!)
  if (!cps.length) return ''
  const withFe = cps.map(hex).join('-')
  if (EMOJI3D_FILES.has(withFe)) return withFe
  const noFe = cps.filter((c) => c !== FE0F).map(hex).join('-')
  if (EMOJI3D_FILES.has(noFe)) return noFe
  // Keyboards often drop the invisible FE0F selector (e.g. plain "❤"): put it back after the first symbol.
  const first = cps.filter((c) => c !== FE0F)
  const fixed = [hex(first[0]), 'fe0f', ...first.slice(1).map(hex)].join('-')
  if (EMOJI3D_FILES.has(fixed)) return fixed
  return ''
}

export const emoji3dSrc = (name: string) => `/emoji3d/${name}.webp`

const KEYCAP = /^[0-9#*]\uFE0F?\u20E3$/
const FLAG = /^[\u{1F1E6}-\u{1F1FF}]{2}$/u
const PICT = /\p{Extended_Pictographic}/u
const PRESENT = /\p{Emoji_Presentation}/u

/** Is this single grapheme really an emoji (and not a plain symbol like © or ™ typed as text)? */
function looksLikeEmoji(g: string): boolean {
  if (KEYCAP.test(g) || FLAG.test(g)) return true
  if (!PICT.test(g)) return false
  if (g.includes('\uFE0F') || g.includes('\u200D') || PRESENT.test(g) || g.codePointAt(0)! >= 0x1f000) return true
  // A lone symbol typed without the invisible FE0F selector (plain ❤ ✔ ☀ ...): treat as emoji, except the text marks (c) (R) (TM).
  const cp = g.codePointAt(0)!
  return cp !== 0xa9 && cp !== 0xae && cp !== 0x2122
}

const segmenter: { segment: (s: string) => Iterable<{ segment: string }> } | null =
  typeof Intl !== 'undefined' && (Intl as any).Segmenter ? new (Intl as any).Segmenter(undefined, { granularity: 'grapheme' }) : null

export type EmojiPart = { t: 'text'; v: string } | { t: 'emoji'; v: string; name: string }

/** Splits text into plain-text runs and emoji (only those we have a 3D picture for). */
export function splitEmoji(text: string): EmojiPart[] {
  const out: EmojiPart[] = []
  if (!text) return out
  // Fast path: no emoji-ish character at all -> one text part (most messages).
  if (!/[\u00a9\u00ae\u203c-\u3299\u{1F000}-\u{1FAFF}\uFE0F\u20E3]/u.test(text)) return [{ t: 'text', v: text }]
  const graphemes = segmenter ? Array.from(segmenter.segment(text), (s) => s.segment) : Array.from(text)
  let buf = ''
  for (const g of graphemes) {
    const name = looksLikeEmoji(g) ? emoji3dName(g) : ''
    if (name) {
      if (buf) { out.push({ t: 'text', v: buf }); buf = '' }
      out.push({ t: 'emoji', v: g, name })
    } else buf += g
  }
  if (buf) out.push({ t: 'text', v: buf })
  return out
}

/** True when the whole text is just 1-3 emoji (no card / bubble is drawn around such a message). */
export const isJumboText = (text: string | undefined | null): boolean => !!text && jumboCount(splitEmoji(String(text))) > 0

/** 1-3 emoji and nothing else (spaces allowed) -> shown big, like Telegram. Returns the count, or 0. */
export function jumboCount(parts: EmojiPart[]): number {
  let n = 0
  for (const p of parts) {
    if (p.t === 'emoji') n++
    else if (p.v.trim()) return 0
  }
  return n >= 1 && n <= 3 ? n : 0
}
